/**
 * Job detail: day-of customer messages (tracker item 43). For each message
 * ("On my way", "Job complete"): whether it is on for the company, the last
 * one sent for this job, and a "Notify customer" button (with the reason
 * when it cannot be used). Below, the job's message history. ADMIN and
 * OFFICE_MANAGER (the page only renders it for office roles).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { INotificationsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { secondaryButtonClass } from './formStyles';
import { OutboxStatusBadge } from './NotificationBadges';
import { eventLabel, messageStatusText, newIdempotencyKey, NOTIFICATION_SETTINGS_PATH } from '../pages/settings/notificationUi';
import { Toast } from './Toast';

interface Props {
  api: Pick<INotificationsApi, 'getJobCustomerNotifications' | 'sendJobCustomerNotification'>;
  jobId: string;
  /** Reloads when the job's status changes (e.g. completed) */
  jobStatus: string;
}

const tap = ' min-h-[44px] sm:min-h-0';

function summaryStatus(m: types.DayOfMessageSummary): string {
  return messageStatusText({ status: m.status, skipReasonText: m.skipReasonText, lastError: null, deliveryStatus: null });
}

export function JobCustomerNotificationsSection({ api, jobId, jobStatus }: Props) {
  const [state, setState] = useState<types.JobCustomerNotifications | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await api.getJobCustomerNotifications(jobId));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load customer notifications'));
    }
  }, [api, jobId]);

  useEffect(() => { void load(); }, [load, jobStatus]);

  async function send(event: types.DayOfEventState) {
    if (busy || !event.canSend) return;
    if (!window.confirm(`Email the customer the "${event.label}" message now?`)) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.sendJobCustomerNotification(jobId, event.eventKey, newIdempotencyKey());
      setNotice(
        result.deduplicated
          ? 'This was already queued a moment ago.'
          : result.message.status === 'SKIPPED'
            ? `Not sent: ${result.message.skipReasonText ?? result.message.skipReason}`
            : `Queued to ${result.message.toAddress ?? 'the customer'}.`
      );
      await load();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not send the message'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="customer-notifications-heading" className="bg-white rounded-lg shadow p-5 sm:p-7" data-testid="job-customer-notifications">
      <h2 id="customer-notifications-heading" className="text-lg font-semibold mb-3">Customer notifications</h2>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {state === null ? (
        !error && <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          {!state.events.some((e) => e.enabled) && (
            <p role="note" className="mb-3 rounded bg-gray-50 p-3 text-sm text-gray-700">
              {state.notificationsEnabled ? 'The day-of messages are turned off.' : 'Notifications are turned off for this company.'}{' '}
              <Link to={NOTIFICATION_SETTINGS_PATH} className="text-blue-700 underline">Settings &gt; Notifications</Link>
            </p>
          )}
          <ul className="space-y-3">
            {state.events.map((event) => (
              <li key={event.eventKey} className="flex flex-wrap items-start justify-between gap-3 border border-gray-200 rounded-lg p-3">
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-gray-900">
                    {event.label}
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-xs font-semibold ${event.enabled ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {event.enabled ? 'On' : 'Off'}
                    </span>
                  </p>
                  {event.lastMessage ? (
                    <p className="text-xs text-gray-600 flex flex-wrap items-center gap-1 mt-1">
                      Last: <OutboxStatusBadge status={event.lastMessage.status} /> {new Date(event.lastMessage.createdAt).toLocaleString()}
                      {event.lastMessage.status === 'SKIPPED' && <span>· {summaryStatus(event.lastMessage)}</span>}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Not sent for this job yet.</p>
                  )}
                  {event.canSend && event.wouldSkipText && (
                    <p className="text-xs text-amber-800 mt-1">It would not be sent: {event.wouldSkipText}.</p>
                  )}
                  {!event.canSend && event.unavailableReason && event.enabled && (
                    <p className="text-xs text-gray-500 mt-1">{event.unavailableReason}</p>
                  )}
                </div>
                <button
                  type="button"
                  className={`${secondaryButtonClass}${tap}`}
                  disabled={busy || !event.canSend}
                  title={event.unavailableReason ?? undefined}
                  onClick={() => void send(event)}
                >
                  Notify customer
                </button>
              </li>
            ))}
          </ul>
          {state.messages && state.messages.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-1">Sent for this job</h3>
              <ul className="divide-y divide-gray-100 text-sm">
                {state.messages.map((m) => (
                  <li key={m.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0 break-words">
                      <span className="font-medium text-gray-900">{eventLabel(m.eventKey)}</span>
                      <span className="block text-xs text-gray-500">
                        {new Date(m.createdAt).toLocaleString()}{m.toAddress ? ` · to ${m.toAddress}` : ''}
                      </span>
                    </div>
                    <div className="text-right">
                      <OutboxStatusBadge status={m.status} />
                      {m.status === 'SKIPPED' && <span className="block text-xs text-gray-600">{summaryStatus(m)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
